import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../navbar';
import { productsService } from '../../services/api/products.service';
import { config, buildImageUrl } from '../../config';

function ProductDelete() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProduct = useCallback(async () => {
    try {
      const data = await productsService.getProductById(id);
      setProduct(data);
    } catch (err) {
      setError((err && (err.message || err.error)) || String(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await productsService.deleteProduct(id);
      navigate('/products');
    } catch (err) {
      setError((err && (err.message || err.error)) || String(err));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex">
          <Navbar />
        </div>
        <div className="max-w-2xl mx-auto p-4 mt-20">
          <div className="text-center">Loading...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="flex">
          <Navbar />
        </div>
        <div className="max-w-2xl mx-auto p-4 mt-20">
          <div className="text-red-600">{error}</div>
          <button
            onClick={() => navigate('/products')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Back to Products
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex">
        <Navbar />
      </div>
      <div className="max-w-2xl mx-auto p-4 mt-20">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h1 className="text-2xl font-bold mb-6">Delete Product</h1>
          
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete this product?
            </p>
            
            {product && (
              <div className="bg-gray-50 p-4 rounded">
                <div className="flex items-start space-x-4">
                  <img
                    src={product.images && product.images[0] ? buildImageUrl(product.images[0]) : config.defaultImage}
                    alt={product.name}
                    className="w-24 h-24 object-cover rounded"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = config.defaultImage;
                    }}
                  />
                  <div>
                    <h3 className="font-semibold text-lg">{product.name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{product.description}</p>
                    <p className="text-blue-600 font-semibold mt-2">₹{product.price}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={() => navigate('/products')}
              className="px-4 py-2 text-gray-700 border rounded-md"
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-400"
            >
              {deleting ? 'Deleting...' : 'Delete Product'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductDelete;